import m from "mithril";

const { Sortable } = require("sortablejs")
import Requester from "../../../shared/request";
import Gateway from "../../../shared/app_settings";
import i18n from "../../../shared/i18n/i18n";
import Toasts from "../../../shared/toasts";
import Auth from "../../../shared/auth";
import EditFieldToolbox from "./EditFieldToolbox";
import EditSectionToolbox from "./EditSectionToolbox";
import FormElementsMenu from "./FormElementsMenu";
import sanitizeHtml from "sanitize-html";
import TextUtilities from "../../../shared/utilities/text_utilities";

let loading = false;
let saving = false;
let unit = null;
let originalUnit = null;
const history = [];

const saveForm = () => {
    saving = true;
    Requester.post(Gateway.admin + "units/assessment/" + unit.id + "/content", unit)
        .then(result => {
            unit = result;
            Toasts.add("success", i18n.t("saved_changes"));
        })
        .catch(error => {
            Toasts.add("error", i18n.t("error"), i18n.t(error.response.properties.errorCode));
        })
        .then(() => {
            saving = false;
        });
};

const section = (id, name, information, fields, deps) => ({
    id: id || Math.random().toString(36).slice(2),
    name: name || "",
    information: information || "",
    prerequisiteOptionIds: deps || [],
    fields: fields || []
});

const field = (id, type, name, information, isRequired, options, deps) => ({
    id: id || Math.random().toString(36).slice(2),
    name: name || "",
    information: information || "",
    type: type || "textfield",
    isRequired: isRequired || false,
    prerequisiteOptionIds: deps || [],
    options: options || []
});

function initialiseFormSectionSorting() {
    Sortable.create(document.querySelector(".form-sections"), {
        group: { name: "layout" },
        animation: 100,
        ghostClass: "dragging",
        onSort: evt => {
            let sectionItem;
            const isNew = evt.from.closest(".form-toolbox");

            if (isNew) {
                sectionItem = section(null, i18n.t("new_section"));
            } else {
                const id = evt.item.getAttribute("id");
                for (let i = 0; i < unit.sections.length; i++) {
                    if (unit.sections[i].id === id) {
                        sectionItem = unit.sections[i];
                        unit.sections.splice(i, 1);
                    }
                }
            }

            unit.sections.splice(evt.newIndex, 0, sectionItem);

            if (isNew) {
                if (evt.item && evt.item.parentNode) {
                    evt.item.parentNode.removeChild(evt.item);
                }
                mountSectionToolbox(sectionItem);
            }

            m.redraw();
        }
    });
};

function initialiseFormFieldSorting() {
    let fieldGroups = document.querySelectorAll(".form-section-fields");
    for (let i = 0; i < fieldGroups.length; i++) {
        Sortable.create(fieldGroups[i], {
            group: { name: "fields" },
            animation: 100,
            ghostClass: "dragging",
            onSort: evt => {
                let fieldItem;
                const isNew = evt.from.closest(".form-toolbox");

                // If the field was added from the toolbox
                if (isNew) {
                    const type = evt.item.getAttribute("data-type");
                    fieldItem = field(null, type, i18n.t("new_field"));
                } else {
                    const id = evt.item.getAttribute("id");
                    for (let i = 0; i < unit.sections.length; i++) {
                        for (let x = 0; x < unit.sections[i].fields.length; x++) {
                            if (unit.sections[i].fields[x].id === id) {
                                fieldItem = unit.sections[i].fields[x];
                                unit.sections[i].fields.splice(x, 1);
                            }
                        }
                    }
                }

                const fromSecId = evt.from.parentNode.getAttribute("id");
                const toSecId = evt.to.parentNode.getAttribute("id");

                // Add the field to the section
                for (let i = 0; i < unit.sections.length; i++) {
                    if (unit.sections[i].id === toSecId) {
                        unit.sections[i].fields.splice(evt.newIndex, 0, fieldItem);
                    }
                }

                if (isNew || fromSecId !== toSecId) {
                    if (evt.item && evt.item.parentNode) {
                        evt.item.parentNode.removeChild(evt.item);
                    }
                }

                // Show the field edit toolbox
                if (isNew) {
                    mountFieldToolbox(fieldItem);
                }

                // Make sure the view is up to date
                m.redraw();
            }
        });
    }
};

function undoLast() {
    unit = JSON.parse(JSON.stringify(history.pop()));
};

function mountSectionToolbox(s) {
    const sec = unit.sections.find(e => e.id === s.id);
    if (sec) {
        m.mount(document.querySelector(".form-toolbox"), {
            view: () => m(EditSectionToolbox, { sec: sec, unit: unit, undoHistory: history })
        });
    }
};

function mountFieldToolbox(f) {
    let fld;
    for (let i = 0; i < unit.sections.length; i++) {
        for (let x = 0; x < unit.sections[i].fields.length; x++) {
            if (unit.sections[i].fields[x].id === f.id) {
                fld = unit.sections[i].fields[x];
            }
        }
    }
    if (fld) {
        m.mount(document.querySelector(".form-toolbox"), {
            view: () => m(EditFieldToolbox, { fld: fld, unit: unit, undoHistory: history })
        });
    }
};

function renderFieldInput(field) {
    if (field.type === "textfield") return m("input[type=text][disabled]", {placeholder: field.information});
    if (field.type === "textbox")   return m("textarea[disabled]", {placeholder: field.information});
    if (field.type === "email")     return m("input[type=email][disabled]", {placeholder: i18n.t("enter_email")});
    if (field.type === "number")    return m("input[type=number][disabled]", {placeholder: field.information});
    if (field.type === "date")      return m("input[type=text][disabled]", {placeholder: field.information});
    if (field.type === "checkbox") {
        return field.options.map(option => m(".checkbox-section",
            m(".div", m("i.icon-checkbox-empty text-gray"), m("span.ml-1", option.value))
        ));
    }
    if (field.type === "radio") {
        return field.options.map(option => m(".checkbox-section",
            m(".div", m(".radio-button text-gray"), m("span.ml-1", option.value))
        ));
    }
    return "";
}

const Component_Unit_Assessment_Editor = {
    oninit: function (vnode) {
        unit = vnode.attrs.unit.properties;
        originalUnit = JSON.parse(JSON.stringify(unit));
    },
    view: function () {
        return [
            m(".content-box", [
                !loading ? [
                    unit ? m(".flex-row", [
                        m(".form-toolbox-container", { style: "width: 30%; max-width: 400px; min-width: 300px;" }, [
                            Auth.validateRights(["unit_form_edit"], [
                                m(".toolbar mb-2", [
                                    m(".toolbar-section", [
                                        m("button.with-icon btn-cta", {
                                            onclick: saveForm,
                                            class: unit === originalUnit || saving ? "disabled" : ""
                                        }, [m("i.icon-check"), i18n.t("save_checklist")]),
                                        m("button.with-icon btn-error", {
                                            onclick: undoLast,
                                            class: history.length > 0 ? "" : "disabled"
                                        }, [m("i.icon-undo"), i18n.t("undo_last")])
                                    ])
                                ])
                            ]),
                            m(".form-toolbox", m(FormElementsMenu))
                        ]),
                        m(".flex-auto flex-column ml-2", [
                            m("h3.text-center", i18n.t("assessment_form_editor")),
                            m(".form-unit form-preview", [
                                m(".mb-2", [
                                    m(".text-bold", unit.name),
                                    TextUtilities.hasVisibleText(unit.instructions) ? m("div.mb-2.instructions", m.trust(sanitizeHtml(unit.instructions || "",
                                        {
                                            allowedTags: sanitizeHtml.defaults.allowedTags, allowedAttributes:
                                                {"*": ["style", "class"], "a": ["href", "target"]}
                                        }))
                                    ) : "",
                                ]),
                                m(".form-sections form full-width", {
                                    oncreate: Auth.validateRights(["unit_form_edit"], () => initialiseFormSectionSorting())
                                }, [
                                    unit.sections.length > 0 ? unit.sections.map(section => m(".form-section mb-2 full-width", {
                                        key: section.id,
                                        id: section.id,
                                        onclick: () => mountSectionToolbox(section)
                                    }, [
                                        m(".form-label.text-bold.richtext", m.trust(sanitizeHtml(section.name || "", {allowedTags: sanitizeHtml.defaults.allowedTags, allowedAttributes: {"*": ["style", "class"], "a": ["href", "target"]}}))),
                                        section.information ? m(".form-subtitle.richtext.mb-3", m.trust(sanitizeHtml(section.information || "", {allowedTags: sanitizeHtml.defaults.allowedTags, allowedAttributes: {"*": ["style", "class"], "a": ["href", "target"]}}))) : "",
                                        m(".form form-section-fields full-width", {
                                            oncreate: Auth.validateRights(["unit_form_edit"], () => initialiseFormFieldSorting())
                                        }, [
                                            section.fields.map(field => m(".form-section", {
                                                key: field.id,
                                                id: field.id,
                                                onclick: e => {
                                                    e.stopPropagation();
                                                    mountFieldToolbox(field);
                                                }
                                            }, [
                                                m(".form-label.richtext", m.trust(field.name || "")),
                                                renderFieldInput(field)
                                            ]))
                                        ])
                                    ])) : m(".text-gray", i18n.t("drag_section_here"))
                                ])
                            ])
                        ])
                    ]) : m(".text-gray padding", i18n.t("unable_to_load_unit"))
                ] : m(".text-gray padding", [i18n.t("loading"), m(".loading-dots")])
            ])
        ];
    }
};

export default Component_Unit_Assessment_Editor;
